import React, { useEffect, useState } from 'react';
import Row from '@amzn/meridian/row';
import Textarea from '@amzn/meridian/textarea';
import RadioButton from '@amzn/meridian/radio-button';
import Box from '@amzn/meridian/box';
import YesCheck from './YesCheck';
import NoX from './NoX';
import styles from './MeetsTheBar.module.css';
import { FormattedMessage } from 'react-intl';
import { I18NContent } from '../../api/CoachingNotes/schema';

type MeetsTheBarDataItem = {
  comments: string;
  checkedValue: boolean | undefined;
};

interface MeetsTheBarProps {
  bulletPoints?: {
    metTheBar: I18NContent[];
    belowTheBar: I18NContent[];
  };
  onChange: (args: MeetsTheBarDataItem) => void;
  heading: I18NContent;
  forceResetState?: number;
  placeholder?: string;
  data: MeetsTheBarDataItem | undefined;
  csaFields?: (...args: string[]) => { [key: string]: string };
  disabled?: boolean;
}

const MeetsTheBar = ({
  bulletPoints,
  onChange,
  heading,
  forceResetState,
  placeholder,
  data,
  csaFields = (...args: string[]) => ({}),
  disabled,
}: MeetsTheBarProps) => {
  const [checkedValue, setCheckedValue] = useState<boolean | undefined>(
    data?.checkedValue
  );
  const [comments, setComments] = useState(data?.comments ?? '');

  const handleChange = (dataType: string) => (data: any) => {
    switch (dataType) {
      case 'comments':
        setComments(data);
        break;
      case 'radio':
        setCheckedValue(data);
        break;
    }
  };

  useEffect(() => {
    handleChange('comments')(data?.comments);
    handleChange('radio')(data?.checkedValue);
  }, [data]);

  useEffect(() => {
    onChange({ checkedValue, comments });
  }, [checkedValue]);

  useEffect(() => {
    if (!data) {
      setComments('');
      setCheckedValue(undefined);
    }
  }, [forceResetState]);

  return (
    <Box className={styles.meetsTheBarContainer}>
      <Row>
        <ul>
          <li className={styles.meetsTheBarTitle}>
            <FormattedMessage
              defaultMessage={heading.defaultMessage}
              id={heading.translationID}
            />
          </li>
        </ul>
      </Row>
      <div className={styles.radioButtonRow}>
        <div className={styles.metTheBarColumn}>
          <RadioButton
            checked={checkedValue === undefined ? undefined : checkedValue}
            onChange={handleChange('radio')}
            disabled={disabled}
            name='meets-the-bar'
            value={true}
            {...csaFields(
              'radio-button',
              'meets the bar',
              `cs_perfportal_tx_monitoring_meets_the_bar`
            )}
          >
            <YesCheck
              tooltip='Meets the bar'
              className={styles.yesCheckGreen}
            />{' '}
            <span className={styles.iconDescriptor}>
              <FormattedMessage
                id='cs_perfportal_tx_monitoring_meets_bar'
                defaultMessage='Meets the bar'
              />
            </span>
          </RadioButton>
        </div>
        <div className={styles.didNotMeetTheBarColumn}>
          <RadioButton
            disabled={disabled}
            checked={checkedValue === undefined ? undefined : !checkedValue}
            onChange={handleChange('radio')}
            name='does-not-meet-the-bar'
            value={false}
            {...csaFields(
              'radio-button',
              'below the bar',
              `cs_perfportal_tx_monitoring_below_the_bar`
            )}
          >
            <NoX tooltip='Below the bar' className={styles.noXRed} />{' '}
            <span className={styles.iconDescriptor}>
              <FormattedMessage
                id='cs_perfportal_tx_monitoring_does_not_meet_bar'
                defaultMessage="Doesn't meet the bar"
              />
            </span>
          </RadioButton>
        </div>
      </div>
      <div>
        <div className={styles.metTheBarColumn}>
          <ul className={styles.descriptorLeftUL}>
            {bulletPoints?.metTheBar.map(point => (
              <li className={styles.descriptorListItem}>
                <FormattedMessage
                  id={point.translationID}
                  defaultMessage={point.defaultMessage}
                />
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.didNotMeetTheBarColumn}>
          <ul className={styles.descriptorRightUL}>
            {bulletPoints?.belowTheBar.map(point => (
              <li className={styles.descriptorListItem}>
                <FormattedMessage
                  id={point.translationID}
                  defaultMessage={point.defaultMessage}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className={styles.textAreaContainer}>
        <Textarea
          value={comments}
          disabled={disabled}
          onChange={handleChange('comments')}
          placeholder={placeholder}
          label=''
          width='100%'
          error={false}
          helperText=''
          onBlur={() => {
            onChange({ checkedValue, comments });
          }}
        />
      </div>
    </Box>
  );
};

export default MeetsTheBar;
